import { ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Forgot, Profile, Reset, SignIn, Verify } from "../features/auth";
import { Role } from "../features/auth/types/authType";
import { RoleTypes } from "../features/auth/types/roleTypes";
import Dashboard from "../features/dashboard/Dashboard";
import Error from "../features/error/Error";
import { Candidates } from "../features/process/Candidates";
import { EmployeeDetailView } from "../features/process/EmployeeDetailView";
import { EmployeeEntry } from "../features/process/EmployeeEntry";
import { Employees } from "../features/process/Employees";
import { Companies } from "../features/setups/Companies";
import { CompanyProfile } from "../features/setups/CompanyProfile";
import { Users } from "../features/setups/users";
import DashboardLayout from "./DashboardLayout";
import MainLayout from "./MainLayout";
import ComplaintsList from "../features/complaints/ComplaintsList";
import ComplaintForm from "../features/complaints/ComplaintForm";
import ComplaintDetail from "../features/complaints/ComplaintDetail";
import Tutors from "./tutors/Tutors";
import TutorDetail from "./tutors/TutorDetail";
import Support from "./tutors/Support";
import ImportantLinks from "../features/process/ImportantLinks";
import { Reports } from "./reports/Reports";
import PremiumTemplate from "../features/process/premiumCvs/PremiumTemplate";
import PremiumTemplateMaker from "../features/process/premiumCvs/PremiumTemplateMaker";
import PremiumTemplateCarousel from "../features/process/premiumCvs/PremiumTemplateCarousel";
import LandingPage from "../features/home/LandingPage";
import { CheckStatus } from "../features/process/CheckStatus";
import { CandidateIntake } from "../features/process/CandidateIntake";
import Wall from "../features/wall/Wall";
import EmployeeVideos from "../features/videos/EmployeeVideos";

let PrivilegedRoles: Role[] = [];
const routes = (isLoggedIn: Boolean, roles: Role[], defaultAppPath: string) => {
  PrivilegedRoles = roles;
  return [
    {
      path: "/app",
      element: isLoggedIn ? <DashboardLayout /> : <Navigate to="/home" />,
      children: [
        {
          path: "dashboard",
          element: isPrivileged(<Dashboard />, RoleTypes.ViewDashboard),
        },
        {
          path: "wall",
          element: isPrivileged(<Wall />, RoleTypes.ViewWall),
        },

        {
          path: "profile",
          element: isPrivileged(<Profile />, RoleTypes.CompanyProfile),
        },
        { path: "tutors", element: <Tutors /> },
        { path: "tutor/:id", element: <TutorDetail /> },
        { path: "support", element: <Support /> },
        {
          path: "complaints",
          element: canAccessComplaints() ? (
            <ComplaintsList />
          ) : (
            <Navigate to="/app/support" />
          ),
        },
        {
          path: "complaints/new",
          element: isPrivileged(<ComplaintForm />, RoleTypes.OpenComplains),
        },
        {
          path: "complaints/:id",
          element: canAccessComplaints() ? (
            <ComplaintDetail />
          ) : (
            <Navigate to="/app/support" />
          ),
        },
        {
          path: "complaints/:id/edit",
          element: isPrivileged(<ComplaintForm />, RoleTypes.EditComplains),
        },
        { path: "links", element: <ImportantLinks /> },
        {
          path: "reports",
          element: isPrivileged(<Reports />, RoleTypes.ViewReports),
        },
        {
          path: "cp",
          element: isPrivileged(
            <CompanyProfile />,
            RoleTypes.CompanyManagement,
          ),
        },
        { path: "cp/:id/:type", element: <CompanyProfile /> },
        {
          path: "cvs",
          element: isPrivileged(
            <Candidates isPremium={false} />,
            RoleTypes.ViewCandidates,
          ),
        },
        // { path: "cvd", element: <CvDetailFormat isPremium={false} /> },
        // { path: "pcv", element: <PremiumCandidates isPremium={true} /> },
        {
          path: "cvp/:empId",
          element: isPrivileged(<PremiumTemplate />, RoleTypes.ViewCv),
        },
        {
          path: "cvm",
          element: isPrivileged(<PremiumTemplateMaker />, RoleTypes.CvMaker),
        },
        {
          path: "employees",
          element: canAccessEmployeesPage() ? (
            <Employees />
          ) : (
            <Navigate to="/app/support" />
          ),
        },
        {
          path: "employee/:id",
          element: isPrivileged(<EmployeeEntry />, [
            RoleTypes.AddEmployees,
            RoleTypes.EditEmployees,
          ]),
        },
        {
          path: "employee-detail/:id",
          element: canViewEmployeeDetail() ? (
            <EmployeeDetailView />
          ) : (
            <Navigate to="/app/support" />
          ),
        },
        {
          path: "videos",
          element: isPrivileged(<EmployeeVideos />, RoleTypes.ViewEmployees),
        },
        {
          path: "register-candidate/:id",
          element: isPrivileged(<CandidateIntake />, RoleTypes.RegisterCandidate),
        },

        {
          path: "users",
          element: isPrivileged(<Users />, RoleTypes.ManageUsers),
        },
        // { path: "user/:id", element: <User /> },
        {
          path: "c",
          element: isPrivileged(<Companies />, RoleTypes.ViewCompanies),
        },
        {
          path: "/app",
          element: <Navigate to={defaultAppPath} />,
        },
        {
          path: "member",
          element: <Outlet />,
          children: [
            { path: "/app/member", element: <Profile /> },
            { path: "/app/member/add", element: <Profile /> },
          ],
        },
      ],
    },

    {
      path: "c/:link",
      element: <PremiumTemplateCarousel />,
    },
    {
      path: "/",
      element: <MainLayout />,
      children: [
        { path: "home", element: <LandingPage /> },
        { path: "check-status", element: <CheckStatus /> },
        { path: "login", element: <SignIn /> },
        // { path: "register", element: <SignUp /> },
        {
          path: "verify/:id/:token/:expires/:signature",
          element: <Verify />,
        },
        // {
        //   path: "ccv/:link",
        //   element: <CvDetailCarousel />,
        // },
        { path: "forgotPassword", element: <Forgot /> },
        { path: "reset/:id/:token", element: <Reset /> },
        { path: "404", element: <Error /> },
        { path: "/", element: <Navigate to="/home" /> },
        { path: "*", element: <Navigate to="/404" /> },
      ],
    },
  ];
};
/** Modules whose roles operate on the employees workspace (list + tabs). */
const WORKSPACE_MODULES = ["Employees", "ProcessControl", "Records"];

const moduleOf = (name: string): string =>
  name.includes(".") ? name.slice(0, name.indexOf(".")) : name;

/**
 * True if the user holds `required`, or — when `required` is a `<Module>.View`
 * permission — any other *non-view* permission in the same module (write
 * implies view). Per-tab `Employees.View*` roles do NOT imply `Employees.View`,
 * so a view-only-tab user can't reach view-gated pages (e.g. employee detail).
 */
function holds(required: string): boolean {
  if (PrivilegedRoles.some((r) => r.displayName === required)) return true;
  if (required.endsWith(".View")) {
    const prefix = moduleOf(required) + ".";
    const viewPrefix = prefix + "View";
    return PrivilegedRoles.some(
      (r) =>
        r.displayName?.startsWith(prefix) &&
        !r.displayName?.startsWith(viewPrefix),
    );
  }
  return false;
}

/** Anyone who can reach the employees workspace (list + pipeline tabs). */
function canAccessEmployeesPage(): boolean {
  return PrivilegedRoles.some((r) =>
    WORKSPACE_MODULES.includes(moduleOf(r.displayName ?? "")),
  );
}

/** Detail view: viewers/searchers plus Records (data-entry) editors. */
function canViewEmployeeDetail(): boolean {
  return PrivilegedRoles.some((r) => {
    const d = r.displayName ?? "";
    return (
      d === "Employees.View" ||
      d === "Employees.Search" ||
      moduleOf(d) === "Records"
    );
  });
}

/** Anyone with a Complaints module role. */
function canAccessComplaints(): boolean {
  return PrivilegedRoles.some((r) =>
    (r.displayName ?? "").startsWith("Complaints."),
  );
}

function isPrivileged(element: ReactNode, privileged: string | string[]): ReactNode {
  const privs = Array.isArray(privileged) ? privileged : [privileged];
  const allowed = privs.some((p) => holds(p));
  return allowed ? element : <Navigate to="/app/support" />;
}
export default routes;
